import React, { Fragment, MutableRefObject, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Image, Popover, notification } from 'antd';
import {
    GO_TO_APPOINTMENTS,
    GO_TO_PRESCRIPTION_PAD_V2,
    PPRouterProps,
    PP_SUB_ROUTES,
    PX_STATE,
} from '../../../../routes/paths';
import {
    getPrescription,
    getPatientPersonalProfile,
    getPatientDetailsMR,
} from '../../../../store/selectors/prescriptionPad';
import UseTemplateDialog from './UseTemplateDialog';
import { setEditPatient } from '../../../../store/actions/session';
import {
    EkaCareIcon,
    MedicalHistory,
    canvasIcon,
    gridDotsIcon,
    medicalRecordsIcon,
    milestonesIcon,
    peopleIcon,
    settingsIcon,
    vaccinationIcon,
} from '../../../common/Icons';
import { track } from '../../../../tracking';
import {
    amIDoctorOnly,
    getPatientByLocalId,
    getSelectedClinicId,
} from '../../../../store/idbHandler';
import openVC from '../../../../pages/videoRef';
import { getAgeInYears, getAgeString } from '../../../../store/utils';
import VideoCallBt from '../../leftPanel/VideoCallBt';
import { ONE_CHAR_GENDER } from '../../../../pages/NewPatient';
import { updateMagicBoxActiveTab } from '../../../../store/actions/prescriptionPad';
import { AntTooltip } from '../../../common/AntTooltip';
import { getPatientPicUrl } from '../../../../pages/utils';
import AttachmentsPad from './padV2/AttachmentsPad';
import { ConfigurePadDialog } from './ConfigurePadDialog';
import { MAGIC_BOX_CONTAINER, MAIN_CONTAINER_ID } from './containers';
import { MAGIC_BOX_COMPONENTS } from '../../../../pages/prescriptionPad/magicBox/MAGIC_BOX_COMPONENTS';
import { MHCWithMilestone, MHCWithVaccination } from '../../../../store/types/medicalHistory';
import {
    getIsMilestoneEnabled,
    getIsTreatmentPlanEnabled,
    getIsVaccinationChartVisible,
} from '../../../../store/selectors/session';
import { Age } from '../../../../store/types/prescription';
import { ALL_KINDS } from '../../../../utils/enums';
import { LANG_CODE_TO_NAME } from '../previewScreen/PreviewPanel';
import { SREV2 } from '../../../../api/pushGenie';
import { EKA_EVENT, dispatch as ekaEventDispatch } from '../../../../bridge/events/dispatch';
import { Events, ON_SHOW_SA } from '../../../../bridge/events/types';
import { AssessmentSummaryTab } from '../../../../pages/PastVisitsRightPanel';
import { getAclViewClassName } from '../../../../restrictions/utils';
import { RelatedProfilesPopoverWrapper } from '../../../RelatedProfiles/RelatedProfilesPopoverWrapper';

export let isMagicContainerOpen = localStorage.getItem('isMagicContainerOpen') || '';
export let isGridPadV3Container = !isMagicContainerOpen ? 'grid-cols-padV3' : '';
let isMagicBoxToggleVisible = isMagicContainerOpen ? '' : 'hidden';

export const magicBoxtoggleHandler = (): void => {
    const mainContainer = document.getElementById(MAIN_CONTAINER_ID);
    const magicBoxContainer = document.getElementById(MAGIC_BOX_CONTAINER);
    const magicBoxToggleButton = document.getElementById(MAGIC_BOX_TOGGLE_BUTTON);

    isGridPadV3Container = mainContainer?.classList?.contains('grid-cols-padV3')
        ? ''
        : 'grid-cols-padV3';
    isMagicContainerOpen = magicBoxContainer?.classList?.contains('hidden') ? '' : 'hidden';
    isMagicBoxToggleVisible = magicBoxToggleButton?.classList?.contains('hidden') ? '' : 'hidden';

    mainContainer?.classList.toggle('grid-cols-padV3');
    magicBoxContainer?.classList.toggle('hidden');
    magicBoxToggleButton?.classList.toggle('hidden');

    localStorage.setItem('isMagicContainerOpen', isMagicContainerOpen);

    track('MAGIC_BOX_TOGGLE');
};

const gridDotsIcons = gridDotsIcon('fill-darwin-neutral-900');
const peopleIcons = peopleIcon('w-17 h-13 fill-darwin-neutral-900');
const vaccination = vaccinationIcon('fill-darwin-neutral-900');

const MAGIC_BOX_TOGGLE_BUTTON = 'magicbox-toggle-button';

export const NavBar: React.FC = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { pxState, subRoute, pxId }: PPRouterProps = useParams();
    const prescription = useSelector(getPrescription);
    const patientPersonalProfile = useSelector(getPatientPersonalProfile);
    const [templateSelectorVisible, setTemplateSelectorVisible] = useState(false);
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [isAttachmentDialogVisible, setisAttachmentDialogVisible] = useState(false);
    const pic: MutableRefObject<string | undefined> = useRef(
        getPatientPicUrl(prescription?.patient?.id),
    );
    const [isAttachmentTootipOpen, setIsAttachmentTootipOpen] = useState(false);
    const isVaccinationChartVisible = useSelector(getIsVaccinationChartVisible);
    const isMilestoneEnabled = useSelector(getIsMilestoneEnabled);
    const isTreatmentPlanEnabled = useSelector(getIsTreatmentPlanEnabled);
    const mr = useSelector(getPatientDetailsMR);
    const showMrPresentGreenDot = mr && mr.data && mr.data.length > 0;

    const px = useSelector(getPrescription);

    useEffect(() => {
        if (!px?.doctor._id) {
            return;
        }
    }, [px?.doctor._id]);

    const [saId, setSaId] = useState<string | undefined>(undefined);
    const [saIds, setSaIds] = useState<Array<string>>([]);

    const onEkaEvents: EventListener = (e: Event): void => {
        const { detail } = e as CustomEvent<Events>;
        console.log(e);

        switch (detail.type) {
            case ON_SHOW_SA:
                setSaId(detail?.sa_id);
                setSaIds(detail?.sa_ids || []);
                break;
        }
    };

    useEffect(() => {
        window.addEventListener(EKA_EVENT, onEkaEvents);

        return () => {
            window.removeEventListener(EKA_EVENT, onEkaEvents);
        };
    }, []);

    const onViewSa = () => {
        if (!px) {
            return;
        }
        track('SA_CLICKED');

        ekaEventDispatch({
            type: 'SHOW_QUESTIONNAIRE_LIST',
            data: {
                apt: {
                    aid: px?.visitId,
                    patient: {
                        id: px?.patient.localId,
                        gen: px?.patient.profile.personal.gender,
                        dob: px?.patient.profile.personal.age?.dob,
                    },
                },
            },
        });
    };

    const setPic = (id?: string) => {
        pic.current = getPatientPicUrl(id);
    };

    useEffect(() => {
        setPic(prescription?.patient?.id);
    }, [prescription?.patient?.id]);

    const onPtEdit = (): void => {
        const ptLocalId = prescription?.patient.localId;
        if (!ptLocalId || !prescription) {
            return;
        }

        getPatientByLocalId(ptLocalId)
            .then((pt) => dispatch(setEditPatient(pt)))
            .catch(console.error);
    };

    const onVideoCall = async (): Promise<void> => {
        if (!prescription || !patientPersonalProfile) {
            return;
        }

        // openVC(prescription.patient.profile.personal.name, prescription.visitId);
    };

    const goToPatientOverview = () => {
        dispatch(updateMagicBoxActiveTab(MAGIC_BOX_COMPONENTS.MEDICAL_HISTORY));
        history.push(GO_TO_PRESCRIPTION_PAD_V2(pxId, pxState, 'patient-overview'));
    };

    const goToRelatedProfiles = () => {
        history.push(GO_TO_PRESCRIPTION_PAD_V2(pxId, pxState, 'related-profiles'));
    };

    const goToVaccinationChart = () => {
        history.push(GO_TO_PRESCRIPTION_PAD_V2(pxId, pxState, 'vaccination'));
    };

    const goToPatientMilestones = async () => {
        history.push(GO_TO_PRESCRIPTION_PAD_V2(pxId, pxState, 'milestones'));
    };

    const goToTreatmentPlan = async () => {
        history.push(GO_TO_PRESCRIPTION_PAD_V2(pxId, pxState, 'treatment-plan'));
    };

    const goToCareCanvas = async () => {
        history.push(GO_TO_PRESCRIPTION_PAD_V2(pxId, pxState, 'care-canvas'));
    };

    const goToMedicalRecords = async () => {
        history.push(GO_TO_PRESCRIPTION_PAD_V2(pxId, pxState, 'medical-records'));
    };

    const goToPP = () => {
        dispatch(updateMagicBoxActiveTab(MAGIC_BOX_COMPONENTS.VISITS));
        history.push(GO_TO_PRESCRIPTION_PAD_V2(pxId, pxState));
    };

    const onHome = (): void => {
        const exit = new URLSearchParams(history.location.search).get('exit');
        history.replace(exit || GO_TO_APPOINTMENTS(getSelectedClinicId() || '', 'today-queue'));
        track('PAD_NAV_BAR_BACK');
    };

    const showTemplate = () => {
        setTemplateSelectorVisible(true);
    };

    const onAttachmentPopup = (visible: boolean) => {
        setisAttachmentDialogVisible(visible);
    };

    const { age, gender, name, phone, onApp } = patientPersonalProfile || {};
    const uhid = prescription?.patient?.formData?.find((fd) => fd.key === 'uhid')?.value;

    const ageInYears = getAgeInYears({ dob: age?.dob, month: age?.month });

    const ageString = getAgeString(
        {
            age: age?.value,
            month: age?.month,
            dob: age?.dob,
        },
        undefined,
        '',
    );

    const filteredFormData = prescription?.patient.formData?.filter(
        (data) => data.value && String(data.value).trim() && data.key !== 'salutation',
    );

    const abhaId =
        prescription?.patient['health-ids'] && prescription?.patient['health-ids'].length !== 0
            ? prescription?.patient['health-ids'][0]
            : undefined;

    let content = (
        <div className="space-y-4">
            {filteredFormData?.map((data) => (
                <p>
                    <b>{data.label}:</b>{' '}
                    {data.key === 'lang' ? LANG_CODE_TO_NAME[data.value] || data.value : data.value}
                </p>
            ))}
            {abhaId && (
                <p>
                    <b>ABHA ID:</b> {abhaId}
                </p>
            )}
        </div>
    );

    const salutation = prescription?.patient.formData?.find((data) => data.key === 'salutation');

    if (!filteredFormData?.length && !abhaId) content = <p>No details</p>;

    const getMilestoneMark = () => {
        let hasStatus;

        if (px?.allMilestones) {
            hasStatus = px?.allMilestones?.find((m) => m.status);

            return !!hasStatus;
        }

        if (px?.patientHistory as MHCWithMilestone) {
            hasStatus = (px?.patientHistory as MHCWithMilestone)?.milestones?.find((m) => m.status);

            return !!hasStatus;
        }

        return false;
    };

    const showMilestonemark = getMilestoneMark();

    const onCloseSa = () => {
        setSaId(undefined);
        setSaIds([]);
    };

    const relations = prescription?.patient?.relations;
    const showRelatedProfiles = Array.isArray(relations) && relations.length > 0;
    const pid = prescription?.patient.id || '';

    return (
        <Fragment>
            <div className="col-span-12 bg-neutral-8 h-auto flex flex-col relative pad-shadow-v2">
                <AnimatePresence>
                    {isDialogVisible && (
                        <ConfigurePadDialog closeMe={() => setIsDialogVisible(false)} />
                    )}
                </AnimatePresence>
                {(saId || saIds.length > 0) && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        exit={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={{ zIndex: 20 }}
                        transition={{ duration: 0.2, ease: 'linear' }}
                        className="flex"
                    >
                        <div onClick={onCloseSa} className="modal-overlay" />
                        <div className="flex flex-col modal-content w-80p h-5/6">
                            <div className="flex items-center justify-between p-16 border-b bg-neutral-9 rounded-t-8 border-neutral-7">
                                <p className="font-700 text-neutral-1">Assessment Summary</p>
                                <div
                                    className="flex items-center justify-center w-16 h-16 cursor-pointer"
                                    onClick={onCloseSa}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 320 512"
                                        className="w-20 h-20 fill-neutral-5 hover:fill-neutral-black"
                                    >
                                        <path d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z"></path>
                                    </svg>
                                </div>
                            </div>
                            <AssessmentSummaryTab
                                saId={saId}
                                saIds={saIds}
                                onPad
                                handleCloseSaPopup={onCloseSa}
                            />
                            {/* {section === 'today-queue' || section === 'upcoming'
                                        ? this.UPCOMING_SA_DIA_CTA(saAppointment)
                                        : this.CLOSE_SA_DIA_CTA()} */}
                        </div>
                    </motion.div>
                )}
                <div
                    className="flex justify-between h-52 px-16 bg-white font-lato"
                    style={{
                        zoom: 0.95,
                    }}
                >
                    <div className="pb-4 flex space-x-12 lg:min-w-30p">
                        <div
                            onClick={onHome}
                            className="flex justify-center items-center min-w-24 h-24 bg-darwin-neutral-900 rounded-full mt-6 cursor-pointer"
                        >
                            <svg
                                role="img"
                                viewBox="0 0 9 8"
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-9 h-10 fill-white"
                            >
                                <path d="M8.85547 4.25C8.85547 4.60156 8.58203 4.875 8.25 4.875H2.25391L4.30469 6.94531C4.55859 7.17969 4.55859 7.58984 4.30469 7.82422C4.1875 7.94141 4.03125 8 3.875 8C3.69922 8 3.54297 7.94141 3.42578 7.82422L0.300781 4.69922C0.046875 4.46484 0.046875 4.05469 0.300781 3.82031L3.42578 0.695312C3.66016 0.441406 4.07031 0.441406 4.30469 0.695312C4.55859 0.929688 4.55859 1.33984 4.30469 1.57422L2.25391 3.625H8.25C8.58203 3.625 8.85547 3.91797 8.85547 4.25Z"></path>
                            </svg>
                        </div>
                        <div className="flex items-center">
                            <Image
                                width={40}
                                height={40}
                                src={pic.current}
                                className="bg-neutral-black border border-light rounded-full"
                                preview={{
                                    mask: (
                                        <span
                                            role="img"
                                            aria-label="eye"
                                            className="anticon anticon-eye"
                                        >
                                            <svg
                                                viewBox="64 64 896 896"
                                                focusable="false"
                                                data-icon="eye"
                                                width="1em"
                                                height="1em"
                                                fill="currentColor"
                                                aria-hidden="true"
                                            >
                                                <path d="M942.2 486.2C847.4 286.5 704.1 186 512 186c-192.2 0-335.4 100.5-430.2 300.3a60.3 60.3 0 000 51.5C176.6 737.5 319.9 838 512 838c192.2 0 335.4-100.5 430.2-300.3 7.7-16.2 7.7-35 0-51.5zM512 766c-161.3 0-279.4-81.8-362.7-254C232.6 339.8 350.7 258 512 258c161.3 0 279.4 81.8 362.7 254C791.5 684.2 673.4 766 512 766zm-4-430c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176zm0 288c-61.9 0-112-50.1-112-112s50.1-112 112-112 112 50.1 112 112-50.1 112-112 112z"></path>
                                            </svg>
                                        </span>
                                    ),
                                    maskClassName: 'profile-pic-mask',
                                }}
                            />
                        </div>
                        <Popover placement="bottomLeft" content={content} trigger="click">
                            <div className="flex-col space-y-3 pr-11 pl-1 cursor-pointer">
                                <div className="flex items-center space-x-8 font-700 pt-4">
                                    {prescription ? (
                                        <div className="flex space-x-4 items-center">
                                            {onApp && (
                                                <AntTooltip
                                                    placement="bottom"
                                                    content="Connected on eka.care app"
                                                >
                                                    <span className="w-16 h-16 mt-2 pt-app-tooltip-pad">
                                                        <EkaCareIcon />
                                                    </span>
                                                </AntTooltip>
                                            )}
                                            <RelatedProfilesPopoverWrapper
                                                pid={pid}
                                                placement="bottomRight"
                                                from="rx-pad-name-click"
                                            >
                                                <h2
                                                    className="text-16"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {salutation?.value && (
                                                        <span className="mr-4">
                                                            {salutation?.value}
                                                        </span>
                                                    )}
                                                    <span>{name}</span>
                                                </h2>
                                            </RelatedProfilesPopoverWrapper>
                                        </div>
                                    ) : (
                                        <div className="mt-1 w-84 h-10 shimmer"></div>
                                    )}
                                    {prescription ? (
                                        <span className="text-12 font-400 text-darwin-neutral-800">
                                            {ageString ? `${ageString} | ` : ''}
                                            {gender
                                                ? ONE_CHAR_GENDER[gender?.toLocaleLowerCase()]
                                                : ''}
                                        </span>
                                    ) : (
                                        <div className="mt-1 w-64 h-10 shimmer"></div>
                                    )}
                                </div>
                                <div className="flex items-center space-x-8 text-12 font-400 text-darwin-neutral-800">
                                    {prescription ? (
                                        uhid && (
                                            <>
                                                <span>{uhid}</span>
                                                <span>
                                                    <svg
                                                        role="img"
                                                        viewBox="0 0 4 4"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="h-4 w-4"
                                                    >
                                                        <circle
                                                            cx="2"
                                                            cy="2"
                                                            r="2"
                                                            className="fill-darwin-neutral-800"
                                                        ></circle>
                                                    </svg>
                                                </span>
                                            </>
                                        )
                                    ) : (
                                        <div className="mt-1 w-56 h-10 shimmer"></div>
                                    )}
                                    {prescription ? (
                                        <span
                                            className={getAclViewClassName(
                                                SREV2.PATIENT_MOBILE_NUMBER,
                                            )}
                                        >
                                            {phone?.c && phone?.n ? `${phone.c} ${phone.n}` : ''}
                                        </span>
                                    ) : (
                                        <div className="mt-1 w-72 h-10 shimmer"></div>
                                    )}
                                    <svg
                                        viewBox="0 0 12 7"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-12 h-7"
                                    >
                                        <path
                                            d="M5.75098 6.73438C5.54004 6.73438 5.35254 6.66406 5.21191 6.52344L0.711914 2.02344C0.407227 1.74219 0.407227 1.25 0.711914 0.96875C0.993164 0.664062 1.48535 0.664062 1.7666 0.96875L5.75098 4.92969L9.71191 0.96875C9.99316 0.664062 10.4854 0.664062 10.7666 0.96875C11.0713 1.25 11.0713 1.74219 10.7666 2.02344L6.2666 6.52344C6.12598 6.66406 5.93848 6.73438 5.75098 6.73438Z"
                                            fill="#505775"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </Popover>

                        <div className="flex space-x-12 items-center">
                            <AntTooltip placement="bottom" content={'Edit Patient Details'}>
                                <span
                                    onClick={onPtEdit}
                                    className={`bg-darwin-accent-symptoms-blue-200 h-36 w-36 flex justify-center items-center rounded-4 cursor-pointer ${getAclViewClassName(
                                        SREV2.PATIENT_DATABASE,
                                    )}`}
                                    style={{
                                        visibility: pxState === 'finish' ? 'hidden' : 'visible',
                                    }}
                                >
                                    <svg
                                        role="img"
                                        viewBox="0 0 14 13"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-12 h-12 fill-darwin-accent-symptoms-blue-800"
                                    >
                                        <path d="M12.6484 2.25781L11.2422 0.851562C10.8203 0.40625 10.0938 0.40625 9.64844 0.851562L8.33594 2.16406L11.3125 5.16406L12.6484 3.85156C13.0938 3.40625 13.0938 2.67969 12.6484 2.25781ZM1.72656 8.77344C1.65625 8.82031 1.63281 8.89062 1.60938 8.96094L1 12.0312C0.953125 12.2891 1.14062 12.5 1.35156 12.5C1.375 12.5 1.42188 12.5 1.44531 12.5L4.51562 11.8906C4.58594 11.8672 4.65625 11.8438 4.72656 11.7734L10.7969 5.70312L7.79688 2.70312L1.72656 8.77344ZM4.07031 10.8359L2.3125 11.1875L2.66406 9.42969L3.25 8.84375V10.25H4.65625L4.07031 10.8359Z"></path>
                                    </svg>
                                </span>
                            </AntTooltip>
                            {amIDoctorOnly() && (
                                <span onClick={onVideoCall}>
                                    <VideoCallBt isPadV3={true} aid={prescription?.visitId as string} ptName={name as string} />
                                </span>
                            )}
                        </div>

                        <div
                            className="flex space-x-12 items-center"
                            style={pxState === 'finish' ? { visibility: 'hidden' } : undefined}
                        >
                            <AntTooltip
                                placement="bottom"
                                content={'Attach Files'}
                                visible={pxState === 'finish' ? false : isAttachmentTootipOpen}
                                onVisibleChange={(visible) => {
                                    setIsAttachmentTootipOpen(visible);
                                }}
                                className={getAclViewClassName(SREV2.SEND_ATTACHMENT)}
                            >
                                <span
                                    onClick={() => {
                                        onAttachmentPopup(true);
                                        track('ATTACHMENT_CLIKED');
                                    }}
                                    className="bg-darwin-accent-burgandy-200 h-36 w-36 flex justify-center items-center rounded-4 cursor-pointer"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 640 512"
                                        className="w-18 h-18 fill-darwin-accent-burgandy-800"
                                    >
                                        <path d="M579.8 267.7c56.5-56.5 56.5-148 0-204.5c-50-50-128.8-56.5-186.3-15.4l-1.6 1.1c-14.4 10.3-17.7 30.3-7.4 44.6s30.3 17.7 44.6 7.4l1.6-1.1c32.1-22.9 76-19.3 103.8 8.6c31.5 31.5 31.5 82.5 0 114L422.3 334.8c-31.5 31.5-82.5 31.5-114 0c-27.9-27.9-31.5-71.8-8.6-103.8l1.1-1.6c10.3-14.4 6.9-34.4-7.4-44.6s-34.4-6.9-44.6 7.4l-1.1 1.6C206.5 251.2 213 330 263 380c56.5 56.5 148 56.5 204.5 0L579.8 267.7zM60.2 244.3c-56.5 56.5-56.5 148 0 204.5c50 50 128.8 56.5 186.3 15.4l1.6-1.1c14.4-10.3 17.7-30.3 7.4-44.6s-30.3-17.7-44.6-7.4l-1.6 1.1c-32.1 22.9-76 19.3-103.8-8.6C74 372 74 321 105.5 289.5L217.7 177.2c31.5-31.5 82.5-31.5 114 0c27.9 27.9 31.5 71.8 8.6 103.9l-1.1 1.6c-10.3 14.4-6.9 34.4 7.4 44.6s34.4 6.9 44.6-7.4l1.1-1.6C433.5 260.8 427 182 377 132c-56.5-56.5-148-56.5-204.5 0L60.2 244.3z" />
                                    </svg>
                                </span>
                            </AntTooltip>
                            <AntTooltip placement="bottom" content={'View Assessment'}>
                                <span
                                    onClick={onViewSa}
                                    className="bg-darwin-accent-burgandy-200 h-36 w-36 flex justify-center items-center rounded-4 cursor-pointer"
                                >
                                    <svg
                                        width="9"
                                        height="13"
                                        viewBox="0 0 9 13"
                                        className="w-18 h-18 fill-darwin-accent-burgandy-800"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M1.5 0.5H5.25V3.5C5.25 3.92188 5.57812 4.25 6 4.25H9V11C9 11.8438 8.32031 12.5 7.5 12.5H1.5C0.65625 12.5 0 11.8438 0 11V2C0 1.17969 0.65625 0.5 1.5 0.5ZM6 0.5L9 3.5H6V0.5ZM2.625 6.5C2.41406 6.5 2.25 6.6875 2.25 6.875C2.25 7.08594 2.41406 7.25 2.625 7.25H6.375C6.5625 7.25 6.75 7.08594 6.75 6.875C6.75 6.6875 6.5625 6.5 6.375 6.5H2.625ZM2.625 8C2.41406 8 2.25 8.1875 2.25 8.375C2.25 8.58594 2.41406 8.75 2.625 8.75H6.375C6.5625 8.75 6.75 8.58594 6.75 8.375C6.75 8.1875 6.5625 8 6.375 8H2.625ZM2.625 9.5C2.41406 9.5 2.25 9.6875 2.25 9.875C2.25 10.0859 2.41406 10.25 2.625 10.25H6.375C6.5625 10.25 6.75 10.0859 6.75 9.875C6.75 9.6875 6.5625 9.5 6.375 9.5H2.625Z"
                                            fill="#EC6657"
                                        />
                                    </svg>
                                </span>
                            </AntTooltip>
                        </div>
                        <div className="flex space-x-12 items-center"></div>
                    </div>
                    <div className="flex items-center space-x-12 font-400 text-14">
                        <div
                            style={{ visibility: pxState === 'finish' ? 'hidden' : 'visible' }}
                            className="flex lg:min-w-99 items-center space-x-6 cursor-pointer"
                            onClick={showTemplate}
                        >
                            <span
                                className={`flex relative justify-center items-center ${
                                    templateSelectorVisible
                                        ? 'bg-darwin-neutral-1000'
                                        : 'bg-darwin-neutral-50'
                                } text-darwin-neutral-900 h-24 w-24 rounded-full`}
                            >
                                <svg
                                    role="img"
                                    viewBox="0 0 10 10"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`w-10 h-11 ${
                                        templateSelectorVisible
                                            ? 'fill-white'
                                            : 'fill-darwin-neutral-900'
                                    }`}
                                >
                                    <path d="M9.8125 0.771484V2.14648C9.8125 2.51172 9.49023 2.83398 9.125 2.83398C8.73828 2.83398 8.4375 2.51172 8.4375 2.14648V1.45898H5.6875V8.33398H6.71875C7.08398 8.33398 7.40625 8.63477 7.40625 9.02148C7.40625 9.38672 7.08398 9.6875 6.71875 9.6875H3.28125C2.89453 9.6875 2.59375 9.36523 2.59375 9.02148C2.59375 8.65625 2.89453 8.33398 3.28125 8.33398H4.3125V1.45898H1.5625V2.14648C1.5625 2.51172 1.24023 2.83398 0.875 2.83398C0.488281 2.83398 0.1875 2.51172 0.1875 2.14648V0.771484C0.1875 0.384766 0.488281 0.0839844 0.875 0.0839844H9.125C9.49023 0.0839844 9.8125 0.384766 9.8125 0.771484Z"></path>
                                </svg>
                                {templateSelectorVisible && (
                                    <UseTemplateDialog
                                        key="use-template"
                                        close={() => setTemplateSelectorVisible(false)}
                                        isV2={true}
                                        kind={ALL_KINDS['PX']}
                                        isModal={false}
                                        withPeak={true}
                                    />
                                )}
                            </span>
                            <span
                                className={`text-darwin-neutral-1000 hidden lg:flex select-none ${
                                    templateSelectorVisible ? 'font-700' : ''
                                }`}
                            >
                                Templates
                            </span>
                        </div>

                        <button
                            className="flex items-center space-x-6"
                            onClick={() => setIsDialogVisible(true)}
                        >
                            <span className="flex justify-center items-center bg-darwin-neutral-50 text-darwin-neutral-800 h-24 w-24 rounded-full">
                                {settingsIcon('fill-neutral-3')}
                            </span>
                            <span className="text-darwin-neutral-1000 hidden lg:flex">
                                Configure your pad
                            </span>
                        </button>

                        {pxState === 'edit' && (
                            <button
                                onClick={magicBoxtoggleHandler}
                                className={`flex items-center justify-center w-24 h-24 rounded-full bg-darwin-accent-bright-blue-800 ${isMagicBoxToggleVisible}`}
                                id={MAGIC_BOX_TOGGLE_BUTTON}
                            >
                                <svg
                                    className="w-13 h-13 fill-white"
                                    viewBox="0 0 13 13"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M8.06445 2.97266L9.4707 3.53516L10.0332 4.94141C10.0801 5.01172 10.1504 5.03516 10.2207 5.03516C10.291 5.03516 10.3379 5.01172 10.3848 4.94141L10.9707 3.53516L12.3535 2.97266C12.4238 2.92578 12.4707 2.85547 12.4707 2.78516C12.4707 2.71484 12.4238 2.66797 12.3535 2.62109L10.9707 2.03516L10.3848 0.652344C10.3379 0.582031 10.291 0.535156 10.2207 0.535156C10.1504 0.535156 10.0801 0.582031 10.0332 0.652344L9.4707 2.03516L8.06445 2.62109C7.99414 2.66797 7.9707 2.71484 7.9707 2.78516C7.9707 2.85547 7.99414 2.92578 8.06445 2.97266ZM12.3535 10.1211L10.9707 9.53516L10.3848 8.15234C10.3379 8.08203 10.291 8.03516 10.2207 8.03516C10.1504 8.03516 10.0801 8.08203 10.0332 8.15234L9.4707 9.53516L8.06445 10.1211C7.99414 10.168 7.9707 10.2148 7.9707 10.2852C7.9707 10.3555 7.99414 10.4258 8.06445 10.4727L9.4707 11.0352L10.0332 12.4414C10.0801 12.5117 10.1504 12.5352 10.2207 12.5352C10.291 12.5352 10.3379 12.5117 10.3848 12.4414L10.9707 11.0352L12.3535 10.4727C12.4238 10.4258 12.4707 10.3555 12.4707 10.2852C12.4707 10.2148 12.4238 10.168 12.3535 10.1211ZM9.4707 6.53516C9.4707 6.39453 9.37695 6.25391 9.25977 6.20703L6.61133 4.87109L5.29883 2.24609C5.1582 1.98828 4.75977 1.98828 4.61914 2.24609L3.30664 4.87109L0.658203 6.20703C0.541016 6.25391 0.470703 6.39453 0.470703 6.53516C0.470703 6.67578 0.541016 6.81641 0.658203 6.86328L3.30664 8.19922L4.61914 10.8477C4.68945 10.9648 4.80664 11.0352 4.9707 11.0352C5.08789 11.0352 5.22852 10.9648 5.29883 10.8477L6.61133 8.19922L9.25977 6.86328C9.37695 6.81641 9.4707 6.67578 9.4707 6.53516Z" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
                <div
                    className={`flex justify-center space-x-8 font-400 text-14 bg-white border-t border-darwin-neutral_new-200 ${
                        pxState === 'finish' ? 'hidden' : ''
                    }`}
                >
                    {showRelatedProfiles && (
                        <button
                            className={`py-6 ${
                                subRoute === 'related-profiles'
                                    ? 'active-button'
                                    : 'border-b-2 border-white'
                            } flex items-center space-x-6 px-15`}
                            onClick={goToRelatedProfiles}
                        >
                            {peopleIcons}
                            <span className="text-darwin-neutral-700 hidden lg:flex">
                                Related profiles
                            </span>
                        </button>
                    )}
                    <button
                        className={`py-6 ${
                            subRoute === 'patient-overview'
                                ? 'active-button'
                                : 'border-b-2 border-white'
                        } flex items-center space-x-6 px-15`}
                        onClick={goToPatientOverview}
                    >
                        {gridDotsIcons}
                        <span className="text-darwin-neutral-700 hidden lg:flex">Overview</span>
                    </button>
                    <button
                        className={`py-6 ${
                            subRoute === undefined ? 'active-button' : 'border-b-2 border-white'
                        } flex items-center space-x-6 px-15`}
                        onClick={goToPP}
                    >
                        <svg
                            role="img"
                            viewBox="0 0 11 13"
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-11 h-12 fill-darwin-neutral-900"
                        >
                            <path d="M10.5625 11.0234L8.28906 8.77344L10.5391 6.52344C10.7734 6.28906 10.7734 5.9375 10.5391 5.72656L10.0234 5.1875C9.78906 4.97656 9.4375 4.97656 9.22656 5.1875L7 7.4375L5.40625 5.84375C6.50781 5.32812 7.21094 4.08594 6.92969 2.72656C6.64844 1.41406 5.42969 0.5 4.09375 0.5H0.976562C0.578125 0.5 0.25 0.851562 0.25 1.27344V7.46094C0.25 7.76562 0.484375 8.02344 0.789062 8.02344H1.51562C1.82031 8.02344 2.05469 7.76562 2.05469 7.46094V6.14844H3.03906L5.66406 8.77344L3.41406 11.0234C3.20312 11.2344 3.20312 11.5859 3.41406 11.8203L3.95312 12.3594C4.16406 12.5703 4.51562 12.5703 4.72656 12.3594L7 10.0859L9.25 12.3594C9.46094 12.5703 9.8125 12.5703 10.0469 12.3594L10.5625 11.8203C10.7969 11.5859 10.7969 11.2344 10.5625 11.0234ZM2.10156 2.39844H4.14062C4.65625 2.39844 5.05469 2.82031 5.05469 3.33594C5.05469 3.85156 4.63281 4.27344 4.14062 4.27344H2.10156V2.39844Z"></path>
                        </svg>
                        <span className="text-darwin-neutral-700 hidden lg:flex">Pad</span>
                    </button>
                    {isVaccinationChartVisible && (
                        <button
                            className={`py-6 ${
                                subRoute === 'vaccination'
                                    ? 'active-button'
                                    : 'border-b-2 border-white'
                            } flex items-center gap-4 space-x-6 px-15`}
                            onClick={
                                prescription?.patient.profile.personal.age?.dob ||
                                prescription?.patient.profile.personal.age?.month
                                    ? goToVaccinationChart
                                    : () =>
                                          notification.error({
                                              message: 'Add patient age to view vaccination chart',
                                              duration: 3,
                                          })
                            }
                        >
                            {vaccination}
                            <span className="text-darwin-neutral-700 hidden lg:flex">Vaccines</span>
                            {(px?.patientHistory as MHCWithVaccination)?.vaccinationHistory
                                ?.length || px?.allVaccines?.length ? (
                                <span className="rounded-full p-4 bg-darwin-green-primary"></span>
                            ) : null}
                        </button>
                    )}
                    {isMilestoneEnabled && (
                        <MilestoneBtn
                            age={prescription?.patient.profile.personal.age}
                            goToPatientMilestones={goToPatientMilestones}
                            pxState={pxState}
                            showMilestonemark={showMilestonemark}
                            subRoute={subRoute}
                            disable={ageInYears <= 18 ? false : true}
                        />
                    )}
                    {isTreatmentPlanEnabled && (
                        <button
                            className={`py-6 ${
                                subRoute === 'treatment-plan'
                                    ? 'active-button'
                                    : 'border-b-2 border-white'
                            } flex items-center gap-4 space-x-6 px-15`}
                            onClick={goToTreatmentPlan}
                        >
                            <svg
                                viewBox="0 0 12 13"
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-12 h-12 fill-darwin-neutral-900"
                            >
                                <path d="M4.875 1.25C4.875 0.851562 5.20312 0.5 5.625 0.5H6.375C6.77344 0.5 7.125 0.851562 7.125 1.25V4.55469L9.98438 2.91406C10.3359 2.70312 10.7812 2.82031 10.9922 3.19531L11.3672 3.82812C11.5781 4.20312 11.4609 4.64844 11.1094 4.85938L8.25 6.5L11.1094 8.16406C11.4609 8.375 11.5781 8.82031 11.3672 9.19531L10.9922 9.82812C10.7812 10.2031 10.3359 10.3203 9.98438 10.1094L7.125 8.46875V11.75C7.125 12.1719 6.77344 12.5 6.375 12.5H5.625C5.20312 12.5 4.875 12.1719 4.875 11.75V8.46875L2.01562 10.1094C1.64062 10.3203 1.19531 10.2031 0.984375 9.82812L0.609375 9.19531C0.398438 8.82031 0.515625 8.375 0.890625 8.16406L3.75 6.5L0.890625 4.85938C0.515625 4.64844 0.398438 4.20312 0.609375 3.82812L0.984375 3.19531C1.19531 2.82031 1.64062 2.70312 2.01562 2.91406L4.875 4.55469V1.25Z" />
                            </svg>
                            <span className="text-darwin-neutral-700 hidden lg:flex">
                                Dental Treatment Plan
                            </span>
                        </button>
                    )}
                    <button
                        className={`py-6 ${
                            subRoute === 'care-canvas' ? 'active-button' : 'border-b-2 border-white'
                        } flex items-center gap-4 space-x-6 px-15`}
                        onClick={goToCareCanvas}
                    >
                        {canvasIcon('w-14 h-13 fill-darwin-neutral-900')}
                        <span className="text-darwin-neutral-700 hidden lg:flex">Canvas</span>
                        {px?.careCanvas?.length ? (
                            <span className="rounded-full p-4 bg-darwin-green-primary"></span>
                        ) : null}
                    </button>
                    <button
                        className={`py-6 ${
                            subRoute === 'medical-records'
                                ? 'active-button'
                                : 'border-b-2 border-white'
                        } flex items-center gap-4 space-x-6 px-15`}
                        onClick={goToMedicalRecords}
                    >
                        {medicalRecordsIcon('w-14 h-13 fill-darwin-neutral-900')}
                        <span className="text-darwin-neutral-700 hidden lg:flex">
                            Medical Records
                        </span>
                        {showMrPresentGreenDot ? (
                            <span className="rounded-full p-4 bg-darwin-green-primary"></span>
                        ) : null}
                    </button>
                </div>
                <div id="divShowQuestionnaireList"></div>
            </div>

            <AttachmentsPad
                onAttachmentVisibleChange={onAttachmentPopup}
                isAttachmentDialogVisible={isAttachmentDialogVisible}
            />
        </Fragment>
    );
};

export const MilestoneBtn: React.FC<{
    subRoute: PP_SUB_ROUTES | undefined;
    age?: Age | undefined;
    goToPatientMilestones: () => void;
    pxState: PX_STATE;
    showMilestonemark: boolean;
    disable?: boolean;
}> = ({ subRoute, age, goToPatientMilestones, pxState, showMilestonemark, disable }) => {
    return (
        <button
            className={`py-6 ${
                subRoute === 'milestones' ? 'active-button' : 'border-b-2 border-white'
            } flex items-center gap-4 space-x-6 px-15`}
            onClick={
                disable === true
                    ? () =>
                          notification.error({
                              message: 'Age should be below or equal 18 to view milestones.',
                              duration: 3,
                          })
                    : age?.dob || age?.month
                    ? goToPatientMilestones
                    : () =>
                          notification.error({
                              message: 'Add patient age to view milestones',
                              duration: 3,
                          })
            }
        >
            {milestonesIcon(
                `${disable ? 'fill-darwin-neutral-500' : 'fill-darwin-neutral-900'} h-13 w-12`,
            )}
            <span
                className={`${
                    disable ? 'text-darwin-neutral-500' : 'text-darwin-neutral-700'
                } hidden lg:flex`}
            >
                Milestones
            </span>
            {showMilestonemark ? (
                <span className="rounded-full p-4 bg-darwin-green-primary"></span>
            ) : null}
        </button>
    );
};
